import { useEffect, useState } from 'react';
import { ShieldCheck, Trash2, UserPlus, Crown } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select } from '../components/ui/input';
import { api, type ProjectMember, type MemberRole, type User } from '../lib/api';
import { useCurrentProject } from '../lib/project';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/utils';

const ROLES: MemberRole[] = ['ADMIN', 'MEMBER', 'VIEWER'];

function roleBadgeVariant(role: MemberRole) {
  return role === 'ADMIN' ? 'warning' : role === 'MEMBER' ? 'info' : 'muted';
}

function displayName(m: Pick<ProjectMember, 'user'>) {
  return m.user.name ?? m.user.email;
}

export default function ProjectAccess() {
  const { project } = useCurrentProject();
  const { user: me } = useAuth();
  const [members, setMembers] = useState<ProjectMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [addUserId, setAddUserId] = useState('');
  const [addRole, setAddRole] = useState<MemberRole>('MEMBER');
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!project) return;
    setLoading(true);
    setError(null);
    api.projects.listMembers(project.slug)
      .then(setMembers)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, [project?.slug]);
  useEffect(() => { api.users.list().then(setAllUsers).catch(() => {}); }, []);

  const myMembership = members.find((m) => m.userId === me?.id) ?? null;
  const isUnclaimed = !loading && members.length === 0;
  const isAdmin = myMembership?.role === 'ADMIN';
  const canManage = isUnclaimed || isAdmin;

  const availableUsers = allUsers.filter((u) => !members.some((m) => m.userId === u.id));

  async function claim() {
    if (!project || !me) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.projects.addMember(project.slug, { userId: me.id, role: 'ADMIN' });
      setMembers(updated);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function handleAdd() {
    if (!project || !addUserId) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.projects.addMember(project.slug, { userId: addUserId, role: addRole });
      setMembers(updated);
      setAddUserId('');
      setAddRole('MEMBER');
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function handleRoleChange(userId: string, role: MemberRole) {
    if (!project) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.projects.updateMemberRole(project.slug, userId, role);
      setMembers(updated);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function handleRemove(userId: string) {
    if (!project) return;
    if (!confirm('Remove this member from the project?')) return;
    setBusy(true);
    setError(null);
    try {
      const updated = await api.projects.removeMember(project.slug, userId);
      setMembers(updated);
    } catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-muted max-w-xl">
        Who can see and manage {project ? project.name : 'this project'}'s memory. Roles are per project:
        a member here is not a member of its sub-projects.
      </p>

      {error && (
        <div className="mb-5 px-4 py-3 rounded-md bg-danger-bg border border-danger-ring text-sm text-danger-text">
          {error}
        </div>
      )}

      {loading ? (
        <p className="text-sm text-ink-faint py-10 text-center">Loading…</p>
      ) : isUnclaimed ? (
        <div className="bg-warning-bg border border-warning-ring rounded-lg px-5 py-5 flex items-start gap-4">
          <ShieldCheck size={20} className="text-warning-text flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-warning-text mb-1">This project has no members yet</p>
            <p className="text-xs text-ink-muted mb-3 max-w-lg">
              Nobody — including whoever created it — currently has access. Claim it as ADMIN to start managing who
              else can see and edit its memory.
            </p>
            <Button variant="primary" onClick={claim} disabled={busy}>
              <Crown size={13} className="mr-1.5" />
              {busy ? 'Claiming…' : 'Claim as ADMIN'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          {!isAdmin && (
            <p className="mb-4 text-xs text-ink-faint bg-surface-subtle border border-border-subtle rounded-md px-3 py-2">
              You're a {myMembership?.role.toLowerCase() ?? 'non-'}member of this project — only an ADMIN can add, remove, or re-role members.
            </p>
          )}

          <div className="bg-card rounded-lg border border-border overflow-hidden mb-5">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left">
                  <th className="px-4 py-2.5 text-[11px] font-semibold text-ink-faint uppercase tracking-wide">Member</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold text-ink-faint uppercase tracking-wide">Role</th>
                  <th className="px-4 py-2.5 text-[11px] font-semibold text-ink-faint uppercase tracking-wide">Added</th>
                  <th className="px-4 py-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.userId} className="border-b border-border-subtle last:border-0">
                    <td className="px-4 py-2.5">
                      <p className="text-sm text-ink font-medium">{displayName(m)}</p>
                      <p className="text-[11px] text-ink-faint">{m.user.email}{m.userId === me?.id ? ' · you' : ''}</p>
                    </td>
                    <td className="px-4 py-2.5">
                      {canManage ? (
                        <Select
                          value={m.role}
                          disabled={busy}
                          onChange={(e) => handleRoleChange(m.userId, e.target.value as MemberRole)}
                          className="py-1 text-xs w-28"
                        >
                          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                        </Select>
                      ) : (
                        <Badge variant={roleBadgeVariant(m.role)}>{m.role}</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-ink-faint whitespace-nowrap">{formatDate(m.createdAt)}</td>
                    <td className="px-4 py-2.5 text-right">
                      {canManage && (
                        <button
                          onClick={() => handleRemove(m.userId)}
                          disabled={busy}
                          title="Remove member"
                          className="p-1.5 rounded text-ink-faint hover:text-danger-text hover:bg-danger-bg transition-colors disabled:opacity-40"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {canManage && (
            <div className="bg-surface-subtle border border-border-subtle rounded-lg px-4 py-4">
              <p className="text-[11px] font-semibold text-ink-faint uppercase tracking-wide mb-2.5">Add a member</p>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={addUserId} onChange={(e) => setAddUserId(e.target.value)} disabled={busy || availableUsers.length === 0} className="flex-1 min-w-[200px] max-w-xs">
                  <option value="">{availableUsers.length === 0 ? 'No other users available' : 'Select a user…'}</option>
                  {availableUsers.map((u) => (
                    <option key={u.id} value={u.id}>{u.name ?? u.email}</option>
                  ))}
                </Select>
                <Select value={addRole} onChange={(e) => setAddRole(e.target.value as MemberRole)} disabled={busy} className="w-32">
                  {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                </Select>
                <Button variant="primary" onClick={handleAdd} disabled={busy || !addUserId}>
                  <UserPlus size={13} className="mr-1.5" />
                  Add
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
